import {Component, DestroyRef, computed, inject, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, combineLatest, forkJoin, map, merge, of, startWith, Subject, switchMap, timer} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {AgentEmailPreviewDialogComponent} from '../agent-email-preview/agent-email-preview-dialog.component';
import {environment} from '../../../../environments/environment';
import {DashboardPreviewService} from '../../../core/services/dashboard-preview.service';
import {TransportControlRefreshService} from '../../../core/services/transport-control-refresh.service';
import {controlCapabilityEnabled} from '../../../core/services/crm-control-capabilities';
import {arrivalReminderPath} from '../../../core/services/arrival-status-reminder.service';
import {clientPriceApprovalItems} from '../data-completeness-control/client-price-approval-items';

export interface ControlReminder {id: number; title: string; message: string; path: string; assignment_required: boolean; rule_key?: string; kind?: 'client_price_approval'; request?: {id: number; code: string}; members?: ControlReminder[]}
export interface SharedControlIssue {id: number; record: string; missing_data: string; responsible: string[]; assignment_required: boolean; path: string; rule_key?: string}
export interface ControlReminderResponse {data: {items: ControlReminder[]; control?: {allowed: boolean; items?: SharedControlIssue[]}; capabilities?: unknown}}

@Component({
  selector: 'app-crm-control-banner',
  host: {'[class.has-control-banner]': 'preview.targetId() === null && (commonReview() || items().length > 0)'},
  template: `
    @if (preview.targetId() === null) {
    @if (commonReview()) {
      <section class="control-banner control-summary" role="status" aria-live="polite" aria-label="Data completeness summary">
        <a href="/dashboard/data-completeness-control?category=client-price-approval">Client price to approve: <strong>{{ approvalLabel() }}</strong></a>
        <a href="/dashboard/data-completeness-control?category=transports">Transport data to complete: <strong>{{ transportLabelCount() }}</strong></a>
      </section>
    } @else {
    @if (visibleItems()[selected()]; as item) {
      <section class="control-banner" role="status" aria-live="polite" aria-label="Data completeness reminder">
        <span class="warning" aria-hidden="true">⚠</span>
        <span class="text" [class.grouped-text]="item.members" [title]="item.message">
          @if (item.assignment_required) { <strong>Assign a responsible user: </strong> }
          @if (item.kind === 'client_price_approval' && item.request; as request) {
            Request <a [href]="requestPath(item)" target="_blank" rel="noopener noreferrer">{{ request.code }}</a>: Kristine,
            <button type="button" class="inline-review" [disabled]="busy() || priceReviewOpen()" (click)="reviewPrice(item)">approve the Client price.</button>
          } @else if (item.members) {
            {{ transportLabel(item) }}: Please complete the missing Status check data —
            @for (member of item.members; track member.id; let last = $last) {
              <a [href]="openPath(member)" target="_blank" rel="noopener noreferrer">{{ fieldLabel(member) }}</a>@if (!last) {; }
            }
          } @else { {{ item.title }} }
        </span>
        @if (item.kind === 'client_price_approval') {
          <button type="button" [disabled]="busy() || priceReviewOpen()" (click)="reviewPrice(item)">Review price</button>
        } @else {
          @if (!item.members) { <a [href]="openPath(item)" target="_blank" rel="noopener noreferrer" aria-label="Open missing data in a new tab">Open ↗</a> }
          <button type="button" [disabled]="busy()" (click)="snooze(item)">Remind in 1 hour ×</button>
        }
        @if (visibleItems().length > 1) {
          <button type="button" [disabled]="busy()" (click)="next()" aria-label="Next reminder">{{ selected() + 1 }}/{{ visibleItems().length }} →</button>
        }
      </section>
      @if (error()) { <small role="alert">Could not snooze. Please try again.</small> }
    }
    }
    }
  `,
  styles: [`
    :host { display:block; min-width:0; flex:1; }
    .control-banner { display:flex; align-items:center; gap:10px; padding:6px 12px; border:1px solid #e9c66a; border-radius:7px; background:#fff8e4; color:#4d3c13; font-size:13px; line-height:20px; white-space:normal; }
    .control-summary { flex-direction:column; align-items:flex-start; gap:2px; }
    .control-summary a { font-weight:400; text-decoration:underline; }
    .text { flex:1; min-width:0; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
    .text.grouped-text { display:block; overflow:visible; -webkit-line-clamp:unset; }
    .text a { text-decoration:underline; } .text .inline-review { padding:0; text-align:left; text-decoration:underline; vertical-align:baseline; line-height:inherit; }
    .warning { color:#916400; font-size:19px; }
    a, button { flex-shrink:0; color:#007e78; font:inherit; font-weight:600; }
    a { text-decoration:none; } button { border:0; background:transparent; cursor:pointer; padding:3px; }
    button:disabled { opacity:.5; cursor:wait; } small { display:block; color:#a32121; font-size:11px; }
    @media(max-width:700px) { .control-banner { flex-wrap:wrap; gap:5px; } .text { flex-basis:85%; } }
  `],
})
export class CrmControlBannerComponent {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly refresh = new Subject<void>();
  readonly preview = inject(DashboardPreviewService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly url = environment.baseApi + 'crm-controls/reminders';
  readonly items = signal<ControlReminder[]>([]);
  readonly visibleItems = computed(() => {
    if (environment.production) return this.items();
    const visible: ControlReminder[] = [];
    const transports = new Map<string, ControlReminder[]>();
    for (const item of this.items()) {
      if (item.kind || !/^\/dashboard\/form\/transporting_units\/[1-9]\d*\?top=check_points$/.test(item.path)) {
        visible.push(item);
        continue;
      }
      const members = transports.get(item.path);
      if (members) {
        if (!members.some(member => member.id === item.id)) members.push(item);
      } else {
        transports.set(item.path, [item]);
        visible.push(item);
      }
    }
    return visible.map(item => {
      const members = transports.get(item.path);
      return !item.kind && members && members.length > 1 ? {...item, members,
        assignment_required: members.some(member => member.assignment_required),
        message: members.map(member => member.message).join('\n')} : item;
    });
  });
  readonly selected = signal(0);
  readonly commonReview = signal(false);
  readonly commonCount = signal(0);
  readonly departureCount = signal(0);
  readonly departureLabel = computed(() => this.commonAvailable() ? String(this.departureCount()) : '—');
  readonly priceReminderLinksEnabled = signal(!environment.production);
  readonly transportCount = signal(0);
  readonly transportLabelCount = computed(() => this.commonAvailable() ? String(this.transportCount()) : '—');
  readonly commonAvailable = signal(false);
  readonly commonLabel = computed(() => this.commonAvailable() ? String(this.commonCount()) : '—');
  readonly approvalsAvailable = signal(false);
  readonly approvalCount = computed(() => new Set(this.items().filter(item => item.kind === 'client_price_approval').map(item => item.id)).size);
  readonly approvalLabel = computed(() => !this.approvalsAvailable() ? '—' : this.approvalCount() >= 100 ? '100+' : String(this.approvalCount()));
  readonly departureEnabled = signal(!environment.production);
  readonly busy = signal(false);
  readonly error = signal(false);
  readonly priceReviewOpen = signal(false);
  private revision = 0;

  constructor() {
    merge(timer(0, 30_000), this.refresh, inject(TransportControlRefreshService).refreshed$).pipe(
      switchMap(() => {
        const revision = this.revision;
        if (this.preview.targetId() !== null) return of({data: {items: [] as ControlReminder[]}, revision});
        const controls = this.http.get<ControlReminderResponse>(this.url).pipe(
          map(response => this.controlItems(response)), catchError(error => { this.commonAvailable.set(false); if ([401, 403].includes(error.status)) { this.priceReminderLinksEnabled.set(false); this.commonReview.set(false); } return of([] as ControlReminder[]); }));
        const approvals = this.http.post<{data: {items: ControlReminder[]}}>(
          environment.baseApi + 'entity/search', {entity: 'activities', related_entity: 'activities',
            data: {field: 'template_content', page: 1, client_price_email_preview: true, price_approval_reminders: true}},
        ).pipe(map(response => { this.approvalsAvailable.set(true); return clientPriceApprovalItems(response.data.items); }),
          catchError(() => { this.approvalsAvailable.set(false); return of([] as ControlReminder[]); }));
        return combineLatest({controls: controls.pipe(startWith(this.items().filter(item => item.kind !== 'client_price_approval'))),
          approvals: approvals.pipe(startWith(this.items().filter(item => item.kind === 'client_price_approval')))}).pipe(map(({controls, approvals}) => ({data: {items: [...approvals, ...controls]}, revision})));
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(response => {
      if (response.revision !== this.revision || this.busy()) return;
      this.items.set(response.data.items.filter(item => this.allowedItem(item)));
      if (this.selected() >= this.visibleItems().length) this.selected.set(0);
    });
  }

  /** The reviewer gets aggregate categories; other accounts retain personal reminders. */
  private controlItems(response: ControlReminderResponse): ControlReminder[] {
    const allowed = controlCapabilityEnabled(response.data.capabilities, 'reviewer_navigation', environment.production) && response.data.control?.allowed === true;
    this.priceReminderLinksEnabled.set(controlCapabilityEnabled(response.data.capabilities, 'request_client_price_reminders', environment.production));
    this.departureEnabled.set(controlCapabilityEnabled(response.data.capabilities, 'departure_control', environment.production));
    this.commonReview.set(allowed);
    this.commonAvailable.set(allowed);
    const issues = allowed ? response.data.control?.items ?? [] : [];
    this.commonCount.set(new Set(issues.filter(issue => issue.rule_key !== 'invoiced_truck_departure' && Number.isSafeInteger(issue.id) && issue.id > 0
      && /^\/dashboard\/form\/transporting_units\/[1-9]\d*\?top=check_points$/.test(issue.path))
      .map(issue => `${issue.id}:${issue.path}`)).size);
    this.transportCount.set(new Set(issues.filter(issue => (issue.rule_key !== 'invoiced_truck_departure' || this.departureEnabled())
      && Number.isSafeInteger(issue.id) && issue.id > 0
      && /^\/dashboard\/form\/transporting_units\/[1-9]\d*\?top=check_points$/.test(issue.path)).map(issue => issue.path)).size);
    this.departureCount.set(new Set(issues.filter(issue => issue.rule_key === 'invoiced_truck_departure'
      && Number.isSafeInteger(issue.id) && issue.id > 0
      && /^\/dashboard\/form\/transporting_units\/[1-9]\d*\?top=check_points$/.test(issue.path)).map(issue => issue.id)).size);
    return response.data.items ?? [];
  }

  next(): void { if (this.visibleItems().length) this.selected.update(index => (index + 1) % this.visibleItems().length); }

  transportLabel(item: ControlReminder): string { return item.title.split(':')[0]; }

  fieldLabel(item: ControlReminder): string {
    if (item.rule_key === 'invoiced_truck_departure') return 'Departure from POL';
    if (!item.rule_key || item.rule_key === 'invoiced_truck_arrival') return 'Arrival to final POD';
    return item.title;
  }

  openPath(item: ControlReminder): string { return arrivalReminderPath(item.path, true, this.departureEnabled() && item.rule_key === 'invoiced_truck_departure'); }

  requestPath(item: ControlReminder): string {
    return item.kind === 'client_price_approval' && this.allowedItem(item) ? '/dashboard/form/requests/' + item.request!.id : '';
  }

  reviewPrice(item: ControlReminder): void {
    if (this.priceReviewOpen() || this.preview.targetId() !== null || item.kind !== 'client_price_approval' || !this.allowedItem(item)) return;
    this.priceReviewOpen.set(true);
    this.dialog.open(AgentEmailPreviewDialogComponent, {width: '960px', maxWidth: '96vw', disableClose: true,
      data: {activityId: item.id, kind: 'client', approvalOnly: true, draft: {status: {value: 37}, template: {value: 8}}},
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {this.priceReviewOpen.set(false); this.refresh.next();});
  }

  private allowedItem(item: ControlReminder): boolean {
    const path = item.path;
    if (item.kind === 'client_price_approval') return Number.isSafeInteger(item.id) && item.id > 0
      && path === '/dashboard/form/activities/' + item.id && Number.isSafeInteger(item.request?.id) && item.request!.id > 0
      && typeof item.request?.code === 'string' && item.request.code.trim().length > 0;
    if (item.kind) return false;
    return /^\/dashboard\/form\/transporting_units\/[1-9]\d*\?top=check_points$/.test(path)
      || this.priceReminderLinksEnabled() && /^\/dashboard\/form\/client_prices\/[1-9]\d*$/.test(path);
  }

  snooze(item: ControlReminder): void {
    if (this.busy() || item.kind === 'client_price_approval') return;
    const members = !environment.production && item.members ? item.members : [item];
    const ids = [...new Set(members.filter(member => !member.kind && member.path === item.path
      && this.allowedItem(member) && Number.isSafeInteger(member.id) && member.id > 0).map(member => member.id))];
    if (!ids.length) return;
    this.busy.set(true);
    this.error.set(false);
    this.revision++;
    forkJoin(ids.map(id => this.http.post(this.url + '/' + id + '/snooze', {}).pipe(
      map(() => id), catchError(() => of(null)),
    ))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(results => {
      const succeeded = new Set(results.filter((id): id is number => id !== null));
      this.items.update(items => items.filter(value => !succeeded.has(value.id) || value.kind === 'client_price_approval'));
      this.selected.set(0);
      this.busy.set(false);
      this.error.set(results.some(id => id === null));
    });
  }
}
